import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Drawer, Typography, CircularProgress, Divider } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { ALL_NOTES } from '../utils/musicTheory';
import { MODES } from '../data/contentStore';
import { playFretNote } from '../utils/notePiano';
import { useEntitlement } from '../hooks/useEntitlement';
import { useSignedIn } from '../hooks/useSignedIn';
import { track } from '../utils/analytics';
import BetaCodeDialog from './BetaCodeDialog';
import { accessMessage } from '../lib/accessMessages';
import { readInvitedAccessStatus } from '../lib/beta';
import { canRedeemInviteCode, isInternalBuild } from '../config/distribution';
import { needsAccountBeforePurchase, canRestorePurchases } from '../lib/purchaseGate';
import { useAccessSyncing } from '../data/DataProvider';

// Bottom sheet du contenu verrouille : fait ENTENDRE ce qu'on rate avant de
// proposer l'achat (le verrou est une bande-annonce, pas un mur).
// context: { type: 'mode', mode, rootNote? } | { type: 'root', note } | { type: 'hub' }
// Le contexte 'hub' vient de la carte d'achat du hub "You" : il ne verrouille
// aucun contenu precis, il vend l'app entiere, donc il ne porte aucun item.
//
// Trois traitements du code d'invitation, selon a qui la feuille s'adresse :
//  - build interne : deux chemins a egalite visuelle, separes par un "or". Le
//    code y etait relegue en bas et les testeurs ne le trouvaient pas, alors
//    que c'est leur seule porte. Aucun enjeu de conversion ici.
//  - web public : lien discret, tout en bas, sous le restore. Un chemin de
//    gratuite ne se met jamais sur la route de la caisse : affiche a cote du
//    bouton payant, il envoie une partie des acheteurs chercher un code
//    ailleurs plutot que de payer. Il reste trouvable pour qui en a un.
//  - natif public : rien du tout, decide par canRedeemInviteCode (App Store
//    3.1.1). Pour offrir l'app sur iOS, ce sont les Offer Codes d'Apple, pas un
//    code maison. Les entitlements deja accordes restent reconnus et revalides.
//
// onRequestSignUp : appele apres un code valide. Le deverrouillage est deja pose
// sur cet appareil ; l'inscription qui suit sert a l'emporter ailleurs. C'est le
// parent qui porte cette feuille-la, celle-ci se ferme au meme instant.

const ACCENT = '#f97316';

// Notes jouables de la gamme pour la preview : root en octave 4, intervalles du
// mode, octave suivante quand on passe le B, tonique a l'octave pour conclure.
const previewNotes = (rootNote, mode) => {
  const intervals = MODES[mode]?.intervals;
  const rootIndex = ALL_NOTES.indexOf(rootNote);
  if (!intervals || rootIndex === -1) return [];
  const notes = intervals.map((semi) => {
    const idx = rootIndex + semi;
    return `${ALL_NOTES[idx % 12]}${4 + Math.floor(idx / 12)}`;
  });
  notes.push(`${rootNote}${5 + Math.floor(rootIndex / 12)}`);
  return notes;
};

// onUnlocked : appele avec le contexte au moment ou la feuille se ferme sur un
// succes. L'utilisateur a tape une gamme precise, il doit la trouver a l'ecran
// en revenant, pas celle d'avant. Sans ca, le contenu qu'on vient de payer reste
// a chercher dans la liste.
// initialAskedAccountFirst : le verrou rouvert DEJA revenu de l'etape compte.
// Le detour par Google recharge la page, donc l'etat local ci-dessous n'existe
// plus au retour ; le parent, lui, a garde le fil (usePendingUnlock) et le rend
// ici. Sans quoi la feuille se rouvre a l'identique de celle d'avant le detour,
// et le detour a l'air d'avoir echoue.
const UnlockSheet = ({ open, onClose, context, onRequestSignUp, onUnlocked, initialAskedAccountFirst = false }) => {
  const {
    unlockFullApp,
    restorePurchases,
    priceLabel,
    realPayments,
    patreonEnabled,
    linkPatreon,
    fullApp,
    patreonAccess,
    betaAccess,
  } = useEntitlement();
  const { user, signedIn } = useSignedIn();
  const [busy, setBusy] = useState(false);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [betaOpen, setBetaOpen] = useState(false);
  // Le verrou se souvient qu'il a lui-meme envoye poser un compte avant la
  // caisse (F3) : ce qui revient ensuite est un compte tout neuf, sans acces,
  // et il n'y a rien a lui reprocher (F8b, constat S6).
  const [askedAccountFirst, setAskedAccountFirst] = useState(false);
  // L'etape compte a eu lieu, que ce soit dans cette vie de la feuille ou avant
  // le rechargement. Derive et non recopie dans l'etat : un effet de recopie
  // laisserait passer un rendu peint avec le texte d'avant le detour.
  const sentToAccount = askedAccountFirst || initialAskedAccountFirst;
  const previewTimers = useRef([]);

  const inviteCodeAllowed = canRedeemInviteCode();
  const inviteCodeUpFront = inviteCodeAllowed && isInternalBuild();

  // Compte connecte qui ne porte aucun acces : le seul malentendu courant est de
  // s'etre connecte a un autre compte que celui qui a servi a activer. On nomme
  // donc l'adresse, sans presumer qu'un rachat existe et sans nommer de site.
  // Rien a dire a qui a deja un acces : ce verrou-la est celui d'un contenu que
  // son acces ne couvre pas, pas celui d'un compte vide.
  //
  // Sauf quand ce verrou vient d'envoyer creer ce compte pour acheter : la
  // personne n'a jamais pretendu tenir un acces, lui dire qu'il n'y en a pas
  // sur son compte tout neuf est un reproche sans objet, juste avant de payer.
  const hasSomeAccess = fullApp || patreonAccess || betaAccess;
  // Le pull du compte n'a pas encore rendu son verdict : sur un appareil qui
  // vient d'activer une invitation, il attend la synchro du contenu et met
  // plusieurs secondes. Pendant ce temps `accessMessage` affirmerait qu'aucun
  // acces n'existe sur ce compte, ce qui est faux et se lit comme une panne.
  // On dit ce qu'on est en train de faire, on ne conclut pas.
  const accessSyncing = useAccessSyncing();
  const noAccessMessage =
    signedIn && !hasSomeAccess && !sentToAccount
      ? accessSyncing
        ? 'Checking your access...'
        : accessMessage({ email: user?.email, status: readInvitedAccessStatus(user?.id) })
      : null;

  const openInviteCode = () => {
    track('beta_code_opened', { from: context?.type });
    setBetaOpen(true);
  };

  const stopPreview = () => {
    previewTimers.current.forEach(clearTimeout);
    previewTimers.current = [];
  };

  useEffect(() => {
    if (!open) {
      stopPreview();
      setJustUnlocked(false);
      setBusy(false);
      setPurchaseError(null);
      setAskedAccountFirst(false);
    }
    return stopPreview;
  }, [open]);

  useEffect(() => {
    if (open && context) {
      track('unlock_sheet_opened', {
        gate: context.type,
        item:
          context.type === 'mode'
            ? context.mode
            : context.type === 'root'
              ? context.note
              : 'hub',
      });
    }
  }, [open, context]);

  if (!context) return null;

  const isMode = context.type === 'mode';
  const isHub = context.type === 'hub';
  const previewRoot =
    isMode && ALL_NOTES.includes(context.rootNote) && !context.rootNote.includes('#')
      ? context.rootNote
      : 'C';

  const handlePreview = () => {
    stopPreview();
    track('unlock_sheet_preview', { item: context.mode });
    previewNotes(previewRoot, context.mode).forEach((note, i) => {
      previewTimers.current.push(setTimeout(() => playFretNote(note), i * 220));
    });
  };

  // Le compte AVANT le paiement sur le web (spec v5 F3). La regle et ses
  // raisons vivent dans lib/purchaseGate : elle etait ecrite ici, et le
  // dialogue d'achat de pack l'a contournee en appelant l'achat en direct (S9).
  const needsAccountFirst = needsAccountBeforePurchase({ signedIn });

  // Restore : natif et paiements réels, connecté ou non. La règle et ses
  // raisons vivent dans lib/purchaseGate, partagées avec la page You.
  const showRestore = canRestorePurchases({ realPayments });

  // Le verrou a envoye poser un compte, et le compte est la : on revient de
  // l'etape, il reste la caisse. Cet ecran doit le DIRE, sinon il est
  // strictement identique a celui d'avant le detour et le detour a l'air d'avoir
  // echoue. Constate sur Christophe lui-meme le 29/08/2026, qui a conclu que
  // rien ne s'etait passe.
  const backFromAccountStep = sentToAccount && signedIn;

  const handleUnlock = async () => {
    // La feuille ne se ferme pas : celle du compte s'ouvre par-dessus, et quand
    // elle se retire on retombe ici, sur le bouton d'achat, pret. Rien a
    // rouvrir, rien a retrouver.
    if (needsAccountFirst) {
      track('unlock_sheet_account_first', { from: context.type });
      setAskedAccountFirst(true);
      if (onRequestSignUp) onRequestSignUp('before_purchase');
      return;
    }
    setBusy(true);
    setPurchaseError(null);
    const res = await unlockFullApp({ from: context.type });
    setBusy(false);
    if (res.success) {
      track('full_app_unlocked', { from: context.type });
      setJustUnlocked(true);
      // L'app est deja debloquee sur cet appareil. La proposition de compte qui
      // suit sert a emporter l'achat ailleurs : sans elle, il ne survit qu'au
      // restore du store, donc ni sur une autre plateforme ni sur le web. Elle
      // est facultative, et ne s'affiche pas si un compte existe deja.
      setTimeout(() => {
        if (onUnlocked) onUnlocked(context);
        onClose();
        if (onRequestSignUp) onRequestSignUp('purchase');
      }, 1400);
    } else if (!res.cancelled) {
      setPurchaseError(res.error || 'Purchase failed. Please try again.');
    }
  };

  // Ouvre l'autorisation Patreon. Sur web la page navigue, sur natif un navigateur
  // in-app s'ouvre : dans les deux cas le deverrouillage arrive au retour, pas ici.
  //
  // Meme garde que l'achat (S12, constat verse par S6). Le serveur lie le compte
  // Patreon au user_id du JWT presente : en session anonyme sur le web, l'acces
  // se pose sur le compte anonyme de ce navigateur et n'en sort jamais. F3 ne
  // parlait que du checkout, la porte Patreon menait au meme endroit. Le natif
  // n'est pas concerne, les recettes de build y coupent Patreon (guideline
  // 3.1.3(b)), donc le bouton n'y existe pas.
  const handlePatreon = async () => {
    if (needsAccountFirst) {
      track('unlock_sheet_account_first', { from: context.type });
      setAskedAccountFirst(true);
      if (onRequestSignUp) onRequestSignUp('before_purchase');
      return;
    }
    setBusy(true);
    setPurchaseError(null);
    track('patreon_link_started', { from: context.type });
    const res = await linkPatreon();
    // Sur web la page a deja navigue ; sur natif le navigateur in-app est ouvert
    // par-dessus et l'utilisateur peut revenir sans avoir rien valide.
    setBusy(false);
    if (!res.success) setPurchaseError('Could not reach Patreon. Please try again.');
  };

  const handleRestore = async () => {
    setBusy(true);
    setPurchaseError(null);
    const res = await restorePurchases({ from: context.type });
    setBusy(false);
    if (res.success && res.restored) {
      track('purchases_restored', { from: context.type });
      setJustUnlocked(true);
      setTimeout(() => {
        if (onUnlocked) onUnlocked(context);
        onClose();
      }, 1400);
    } else if (!res.success) {
      setPurchaseError('Restore failed. Please try again.');
    } else {
      setPurchaseError('No previous purchase found for this account.');
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          backgroundColor: '#141420',
          backgroundImage: 'none',
          px: 3,
          pt: 2.5,
          pb: 'calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
          maxWidth: '520px',
          mx: 'auto',
        },
      }}
    >
      {justUnlocked ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <CheckCircleIcon sx={{ fontSize: '3rem', color: '#4ade80', mb: 1 }} />
          <Typography sx={{ color: 'white', fontSize: '1.15rem', fontWeight: 500 }}>
            Everything is unlocked. Enjoy!
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LockIcon sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)' }} />
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              {isMode ? 'Locked scale' : isHub ? 'Full version' : 'Locked key'}
            </Typography>
          </Box>

          <Typography
            sx={{ color: 'white', fontSize: '1.5rem', fontWeight: 600, mb: isMode ? 1 : 2.5 }}
          >
            {isMode
              ? context.mode
              : isHub
                ? 'All keys, all scales, all backings'
                : `${context.note} and every other key`}
          </Typography>

          {/* Une seule phrase, et seulement quand elle mene quelque part : celle
              du mode annonce l'ecoute proposee juste en dessous. Pour une
              tonalite, le titre dit deja tout et le detail du gratuit se lit sur
              les cadenas du selecteur. */}
          {isMode && (
            <Typography
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem', lineHeight: 1.5, mb: 2.5 }}
            >
              Hear its color now. Unlock to see it on the fretboard, get its chords and jam on it.
            </Typography>
          )}

          {isMode && (
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PlayArrowIcon />}
              onClick={handlePreview}
              sx={{
                mb: 1.5,
                py: 1.2,
                textTransform: 'none',
                fontSize: '1rem',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Hear {previewRoot} {context.mode}
            </Button>
          )}

          {/* La trace du detour, juste au-dessus du bouton qui reprend le fil :
              une ligne discrete suffit, elle repond a "est-ce que mon compte est
              bien pris en compte ?" sans occuper l'ecran. */}
          {backFromAccountStep && user?.email && (
            <Typography
              sx={{
                mb: 1,
                textAlign: 'center',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              Signed in as {user.email}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            disabled={busy}
            onClick={handleUnlock}
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              py: 1.4,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600,
              backgroundColor: ACCENT,
              // Encre SOMBRE, comme sur les autres boutons de cet orange
              // (SignUpSheet, GetTheApp) : le blanc y tombe a 2,8:1, sous le
              // seuil AA et meme sous le 3:1 tolere au grand texte, alors que
              // le sombre remonte a 7,5:1. C'etait le dernier bouton reste en
              // blanc, sur l'ecran le plus decisif de l'app.
              color: '#0a0a0a',
              '&:hover': { backgroundColor: '#ea580c', color: '#0a0a0a' },
            }}
          >
            {/* Trois etats pour un seul bouton, parce qu'il ne fait pas la meme
                chose dans les trois cas :
                 - invite sur le web, il ouvre l'etape compte : ne pas promettre
                   un deverrouillage immediat, sinon on decouvre un formulaire
                   la ou on attendait une caisse ;
                 - au retour de cette etape, il mene a la caisse et le dit ;
                 - partout ailleurs (natif, ou compte deja pose), il deverrouille
                   pour de bon, libelle d'origine. */}
            {needsAccountFirst
              ? `Get the full app · ${priceLabel}`
              : backFromAccountStep
                ? `Continue to payment · ${priceLabel}`
                : `Unlock everything · ${priceLabel}`}
          </Button>

          {/* Dit avant le clic ce qui va se passer : personne ne doit decouvrir
              l'etape compte en croyant ouvrir une caisse. "Sign in or create",
              parce que celui qui a deja un compte ailleurs n'en cree pas un
              second. */}
          {needsAccountFirst && (
            <Typography
              sx={{
                mt: 1.25,
                textAlign: 'center',
                fontSize: '0.9rem',
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              You'll sign in or create an account first, so your purchase follows you.
            </Typography>
          )}

          {purchaseError && (
            <Typography
              sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.9rem', color: '#f87171' }}
            >
              {purchaseError}
            </Typography>
          )}

          <Typography
            sx={{
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.9rem',
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            One-time purchase. Every scale, every key, and all the backing tracks included with the app. Yours forever.
          </Typography>

          {/* Build interne seulement : le testeur n'a pas d'autre porte, elle est
              donc a egalite avec l'achat. Sur le web public, le meme chemin
              existe mais en lien discret, apres le restore. */}
          {inviteCodeUpFront && (
            <>
              <Divider
                sx={{
                  my: 2.5,
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.85rem',
                  '&::before, &::after': { borderColor: 'rgba(255,255,255,0.12)' },
                }}
              >
                or
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                disabled={busy}
                startIcon={<VpnKeyIcon />}
                onClick={openInviteCode}
                sx={{
                  py: 1.4,
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: 'white',
                  borderWidth: '1px',
                  borderColor: 'rgba(249,115,22,0.5)',
                  '&:hover': {
                    borderColor: ACCENT,
                    borderWidth: '1px',
                    backgroundColor: 'rgba(249,115,22,0.08)',
                  },
                }}
              >
                I have an invitation code
              </Button>
            </>
          )}

          {/* Un membre de Per a deja paye : il se connecte, il ne rachete pas.
              Bouton nu, sans prix ni lien vers Patreon : la guideline App Store
              3.1.3(b) autorise le login, pas l'incitation a s'abonner ailleurs. */}
          {patreonEnabled && (
            <Button
              fullWidth
              variant="outlined"
              disabled={busy}
              onClick={handlePatreon}
              sx={{
                mt: 2.5,
                py: 1.2,
                textTransform: 'none',
                fontSize: '1rem',
                color: 'white',
                borderColor: 'rgba(255,255,255,0.2)',
                '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Continue with Patreon
            </Button>
          )}

          {/* Le chemin de celui qui tient deja un acces, achete ou non. Sans
              lui, un acheteur iPhone qui ouvre le web se voit reclamer 30 EUR
              une deuxieme fois : "Restore purchases" interroge le store, et sur
              le web il n'y a pas de store, donc rien a retrouver tant qu'aucun
              compte n'est pose. Le libelle ne parle plus d'achat (F8b) : sur
              iPhone, le cas nominal est quelqu'un dont l'acces a ete active sur
              le web et qui n'a jamais rien paye.
              Discret et sous l'achat, comme le code d'invitation : un chemin de
              recuperation ne se met pas sur la route de la caisse.
              Masque pour qui est deja connecte : son compte est charge, s'il
              voit encore ce verrou c'est qu'il n'y a rien a recuperer. */}
          {!signedIn && (
            <Button
              fullWidth
              disabled={busy}
              onClick={() => {
                track('unlock_sheet_restore_signin', { from: context.type });
                onClose();
                if (onRequestSignUp) onRequestSignUp('restore');
              }}
              sx={{
                mt: patreonEnabled ? 0.5 : 2,
                textTransform: 'none',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.65)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Already have access? Sign in
            </Button>
          )}

          {/* Le pendant du bouton ci-dessus, pour qui est deja connecte : la
              meme question ("j'ai pourtant droit a cette app"), une reponse
              differente puisqu'il n'y a plus de compte a retrouver. Une phrase,
              pas un bouton : il n'y a rien a faire ici, tout se passe la ou
              l'acces s'active. */}
          {noAccessMessage && (
            <Typography
              sx={{
                mt: patreonEnabled ? 1.5 : 2,
                textAlign: 'center',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              {noAccessMessage}
            </Typography>
          )}

          {/* Restore : natif et paiements réels, règle et raisons dans
              lib/purchaseGate. Apple l'exige (3.1.1), et il est ouvert aux
              invités : un acheteur sans compte qui réinstalle n'a que ce
              chemin. Il coexiste alors avec "Already have access? Sign in",
              qui répond à l'autre question, celle de l'accès posé ailleurs. */}
          {showRestore && (
            <Button
              fullWidth
              disabled={busy}
              onClick={handleRestore}
              sx={{
                mt: patreonEnabled ? 0.5 : 2,
                textTransform: 'none',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.65)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Restore purchases
            </Button>
          )}

          {/* Dernier element de la feuille, volontairement : invisible pour qui
              vient acheter, trouvable pour qui a recu un code. */}
          {inviteCodeAllowed && !inviteCodeUpFront && (
            <Button
              fullWidth
              disabled={busy}
              onClick={openInviteCode}
              sx={{
                // Colle au bloc precedent des qu'il y en a un, sinon respire.
                mt: showRestore || !signedIn ? 0 : 2,
                textTransform: 'none',
                fontSize: '0.9rem',
                color: 'rgba(255,255,255,0.65)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              }}
            >
              Have an invitation code?
            </Button>
          )}
        </>
      )}
      {inviteCodeAllowed && (
        <BetaCodeDialog
          open={betaOpen}
          onClose={() => setBetaOpen(false)}
          onSuccess={() => {
            if (onUnlocked) onUnlocked(context);
            onClose();
            // Variante nommee (S11) : l'appel partait nu et heritait du defaut
            // du parent, qui vaut 'purchase' ailleurs. Un code d'invitation
            // n'est pas un achat.
            if (onRequestSignUp) onRequestSignUp('beta');
          }}
        />
      )}
    </Drawer>
  );
};

export default UnlockSheet;
